import { useState, useCallback } from 'react';

interface ListingImageProps {
  src: string | null | undefined;
  alt: string;
  className?: string;
  loading?: 'lazy' | 'eager';
}

const PLACEHOLDER_IMAGE = '/images/categories/default-business.jpg';

/**
 * Safe image component with onError fallback to placeholder.
 * Validates URL and handles broken/blocked external images gracefully.
 */
export function ListingImage({ src, alt, className = '', loading = 'lazy' }: ListingImageProps) {
  const [imgSrc, setImgSrc] = useState(() => getSafeImageUrl(src));
  const [hasError, setHasError] = useState(false);

  const handleError = useCallback(() => {
    if (!hasError) {
      setHasError(true);
      setImgSrc(PLACEHOLDER_IMAGE);
    }
  }, [hasError]);

  return (
    <img
      src={imgSrc}
      alt={alt}
      className={className}
      loading={loading}
      onError={handleError}
    />
  );
}

/**
 * Validates and sanitizes image URL.
 * - Blocks external URLs (https://) to avoid 403s from Unsplash etc.
 * - Returns local path or placeholder
 */
function getSafeImageUrl(url: string | null | undefined): string {
  if (!url) return PLACEHOLDER_IMAGE;
  
  const trimmed = url.trim();
  if (!trimmed) return PLACEHOLDER_IMAGE;
  
  // Allow Supabase Storage URLs
  if (trimmed.includes('supabase.co/storage/')) {
    return trimmed;
  }
  
  // Block other external URLs - they cause 403s
  if (trimmed.startsWith('https://') || trimmed.startsWith('http://')) {
    console.warn('[ListingImage] Blocking external URL:', trimmed);
    return PLACEHOLDER_IMAGE;
  }
  
  // Allow data URIs (base64 images uploaded by clients)
  if (trimmed.startsWith('data:image/')) {
    return trimmed;
  }
  
  // Valid local path
  if (trimmed.startsWith('/images/')) {
    return trimmed;
  }
  
  // Unknown format - use placeholder
  return PLACEHOLDER_IMAGE;
}
